import { useEffect, useState } from 'react';

import { Topbar } from '@lcsc-ui/layouts/baseHeader';
import BaseLayout from '@lcsc-ui/layouts/baseLayout';
import useUserStore from '@lcsc-ui/store/user';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import ecpCurl from '@lcsc-ui/utils/ecpCurl';
import Utils from '@lcsc-ui/utils/Utils';
import { ConfigProvider } from 'antd';
import Cookies from 'js-cookie';
import { debounce } from 'lodash-es';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import OpApi from '@/apis/op';
import StaticdataApi from '@/apis/staticdata';
import WwwAPI from '@/apis/www';
import BottomAdContext from '@/context/BottomAdContext';
import IndexContent from '@/features/homeComponents/components/IndexContent';
import TopAlert from '@/features/homeComponents/components/TopAlert';

const DynamicToolBarComponent = dynamic(() => import('@lcsc-ui/components/ToolBar'));

const IndexProvider = ({
  resourceData,
  websiteSectionList,
  catalogList,
  IndexData,
  indexAd,
  headerAd,
  TDKObj,
  indexADList,
  renderSuccess,
}) => {
  const { updateCommonConfigVO, userInfo, updateProjectConfigVO } = useUserStore();
  const [animateShow, setAnimateShow] = useState(false); //动效的显示状态
  const [isLgScreen, setIsLgScreen] = useState(null);

  const handleResize = () => {
    setIsLgScreen(Utils.isLgScreen());
  };

  const mobileRedirect = () => {
    if (Utils.isMobile()) {
      window.location.href = Utils.getMoileUrl(
        'pages/index/index?salesmanCode=' + Utils.getUrlParam('c') + '&ru=' + Utils.getUrlParam('ru')
      );
    }
  };

  function fetchConfig() {
    updateProjectConfigVO(resourceData?.projectConfigVO);
    updateCommonConfigVO(resourceData?.commonConfigVO);
  }

  function getPromotionId() {
    const bd_vid = Utils.getUrlParam('bd_vid');
    if (bd_vid) {
      Cookies.set('cookies_bd_vid', bd_vid, {
        domain: '.szlcsc.com',
        path: '/',
        secure: true,
        httpOnly: false,
      });
    }
    const qhclickid = Utils.getUrlParam('qhclickid');
    if (qhclickid) {
      Cookies.set('cookies_qhclickid', qhclickid, {
        domain: '.szlcsc.com',
        path: '/',
        secure: true,
        httpOnly: false,
      });
    }
    const lcscVid = Utils.getUrlParam('lcsc_vid');
    if (lcscVid) {
      Cookies.set('cookies_lcscvid', lcscVid, {
        domain: '.szlcsc.com',
        path: '/',
        secure: true,
        httpOnly: false,
      });
    }
  }

  useEffect(() => {
    checkFished();
    fetchConfig();
    handleResize();
    mobileRedirect();
    getPromotionId(); // 打开页面，如有推广渠道的，写入推广渠道cookie

    const sid = setTimeout(() => {
      buriedPoint({ ty: 'enter', cp: 'index', op: 'view' });
    }, 1000);

    const debouncedHandleResize = debounce(handleResize, 10);
    window.addEventListener('resize', debouncedHandleResize);
    // 清除事件监听器
    return () => {
      clearTimeout(sid);
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  // 嵌套检查
  const checkFished = () => {
    if (window?.top !== window) {
      console.error('网站被嵌套', window?.top?.location?.href);
    }
  };

  let baseContent = <section className="pb-[60px] bg-[#F6F8FA] pt-[20px]"></section>;
  if (resourceData && IndexData && indexAd) {
    baseContent = (
      <IndexContent
        resourceData={resourceData}
        headerAd={headerAd}
        IndexData={IndexData}
        indexAd={indexAd}
        indexADList={indexADList}
      ></IndexContent>
    );
  }

  return (
    <BaseLayout
      showMemberWebsite
      hideHeader
      resourceData={resourceData}
      websiteSectionList={websiteSectionList}
      catalogList={catalogList}
    >
      <ConfigProvider>
        <Head>
          <link rel="canonical" href="https://www.szlcsc.com/" />
          <title>{TDKObj?.tdkTitle || '立创商城_一站式电子元器件采购自营商城_嘉立创电子商城'}</title>
          <meta
            name="keywords"
            content={TDKObj?.tdkKeywords || '立创商城,元器件,电子元器件,电子元器件商城,电子元器件采购网'}
          />
          {renderSuccess && <meta name="lcscRender" content="true" />}
          <meta
            name="description"
            content={
              TDKObj?.tdkDescription ||
              '立创商城(SZLCSC.COM) 一站式电子元器件采购自营商城，拥有13万+平米智能化仓储，现货SKU品类超56万种，正品保证，支持一键BOM配单，最快4小时发货，为用户提供一站式电子元器件线上采购服务。'
            }
          />
        </Head>
        <TopAlert />
        <Topbar spm="head" userInfo={userInfo}></Topbar>
        <BottomAdContext.Provider value={{ animateShow, setAnimateShow, isLgScreen }}>
          {baseContent}
          {/* 侧边工具栏 */}
          <DynamicToolBarComponent />
        </BottomAdContext.Provider>
      </ConfigProvider>
    </BaseLayout>
  );
};

export async function getServerSideProps() {
  const IndexStaticApiResults = await Promise.allSettled([
    ecpCurl(OpApi.websiteSection),
    ecpCurl(StaticdataApi.resourceList),
    ecpCurl(StaticdataApi.catalogList),
    ecpCurl(WwwAPI.wwwIndex),
    ecpCurl(OpApi.AdvertisingIndex),
    ecpCurl(OpApi.advertisingHeader),
    ecpCurl(OpApi.webTdk),
  ]);

  const [websiteSectionList, resourceData, catalogList, IndexData, indexAd, headerAd, TDKObj] =
    IndexStaticApiResults.map((result) => {
      if (result.status === 'fulfilled') {
        return result.value;
      }
      return null;
    });

  const renderSuccess = IndexStaticApiResults.every((result) => result.status === 'fulfilled');

  return {
    props: {
      resourceData,
      websiteSectionList,
      catalogList,
      IndexData,
      indexAd,
      headerAd,
      TDKObj,
      renderSuccess,
    },
  };
}

export default IndexProvider;
